import {
  CONTAINER_WIDTH_IN_MMM,
  LASHING_BRIDGE_END_CLEARANCE_IN_MM,
  LASHING_BRIDGE_MIN_DOUBLE_GAP_IN_MM,
  LASHING_BRIDGE_RAIL_HEIGHT_TIERS,
  LASHING_BRIDGE_THICKNESS_IN_MM,
  LASHING_BRIDGE_TOP_WALKWAY_DEPTH_IN_MM,
} from "../consts";
import { IBayPattern, IRowPattern } from "../types/IPositionPatterns";
import {
  LASHING_BRIDGE_SIDES,
  TLashingBridgeSide,
  getBayLevelRows,
  lashingBridgeHeightInMm,
  resolveLashingBridgeRows,
} from "./lashingBridgesHelpers";
import {
  getBayEnds,
  getLashingBridgeSlots,
  getLowLongContainerEnds,
  getTypicalLashingBridgeGap,
} from "./getLashingBridgeSlots";
import { pad2, pad3 } from "@tedivo/tedivo-pure-helpers";

import type { BayLcgVcgTcgAndPairings } from "../BayLcgVcgTcgAndPairings/BayLcgVcgTcgAndPairings";
import { BayLevelEnum } from "open-vessel-definition";
import type { BaySizesAndCgs } from "../BayLcgVcgTcgAndPairings/BaySizesAndCgs";

export interface ILashingBridgeRowGeometry {
  row: IRowPattern;
  /** TCG of the row, in mm */
  tcg: number;
  /** Half of the athwartship width covered by this row, in mm */
  halfWidth: number;
  /** Stack bottom of the row (its above-deck bottom base), in mm. Raw: no offsets applied */
  stackBottomBase: number;
  /** Height of the bridge above the stack bottom, in mm */
  heightAboveBottom: number;
  /** Top of the bridge in this row, in mm. `stackBottomBase + heightAboveBottom` */
  top: number;
  /** Top of the drawn structure (handrail included), in mm. Above `top`, see {@link getLashingBridgeFrameLevels} */
  structureTop: number;
  /** Heights of the walkways, in mm, ascending. The last one is the top walkway */
  walkways: number[];
}

export interface ILashingBridgeGeometry {
  isoBay: IBayPattern;
  side: TLashingBridgeSide;
  /** The LCG of the bridge: the one defined in the OVD, or {@link getDefaultLashingBridgeLcg} */
  lcg: number;
  /** True when the OVD doesn't define the LCG and {@link getDefaultLashingBridgeLcg} is used */
  lcgIsDefault: boolean;
  /** LCG of the center of the drawn bridge. The same as `lcg` */
  lcgCenter: number;
  /** Fore-aft depth (FWD to AFT) of the drawn bridge, in mm */
  thickness: number;
  /** Clear fore-aft depth of the top walkway between its handrails, in mm (centered, ≤ `thickness`) */
  topWalkwayDepth: number;
  /** Height of one tier used for the calculations, in mm */
  tierHeight: number;
  /** Rows with a bridge, from port to starboard. Rows with no bridge (`tiers: 0`) are not included */
  rows: ILashingBridgeRowGeometry[];
  minBottomBase: number;
  maxTop: number;
  maxStructureTop: number;
}

export interface IGetLashingBridgesGeometryOptions {
  /** Height of one tier, in mm. Defaults to the average container height of the calculations (8'6") */
  tierHeightInMm?: number;
  /** Fore-aft thickness of the bridges, in mm. Defaults to {@link LASHING_BRIDGE_THICKNESS_IN_MM} */
  thicknessInMm?: number;
  /** Defaults to {@link LASHING_BRIDGE_TOP_WALKWAY_DEPTH_IN_MM} */
  topWalkwayDepthInMm?: number;
  /** Defaults to {@link LASHING_BRIDGE_RAIL_HEIGHT_TIERS} */
  railHeightTiers?: number;
}

/**
 * Geometry (in mm, OVD coordinates) of every lashing bridge of the vessel, to be drawn by the
 * side view and the 3D view. Consumers apply their own offsets (like the bottom base offsets).
 *
 * - LCG: the bridge's `lcg`, or {@link getDefaultLashingBridgeLcg} (the middle of the gap with
 *   the next block of bays).
 * - Height of each row: its `perRow` override or the bridge defaults. `height` wins over `tiers`.
 * - Rows come from {@link getBayLevelRows}. `perRow` keys that the bay doesn't have are ignored.
 * - Frame of each row (structure top and walkways): {@link getLashingBridgeFrameLevels}.
 */
export function getLashingBridgesGeometry(
  p: BayLcgVcgTcgAndPairings,
  options: IGetLashingBridgesGeometryOptions = {},
): ILashingBridgeGeometry[] {
  const tierHeightInMm = options.tierHeightInMm ?? p.averageContainerHeightInMm;
  const thickness = options.thicknessInMm ?? LASHING_BRIDGE_THICKNESS_IN_MM;
  const topWalkwayDepth = Math.min(
    options.topWalkwayDepthInMm ?? LASHING_BRIDGE_TOP_WALKWAY_DEPTH_IN_MM,
    thickness,
  );
  const railHeightTiers = options.railHeightTiers ?? LASHING_BRIDGE_RAIL_HEIGHT_TIERS;

  const result: ILashingBridgeGeometry[] = [];

  p.bayLevelPositionsAbove.forEach((blp) => {
    const info = blp.lashingBridges;
    if (!info) return;

    const rows = getBayLevelRows(p.sizeSummary, blp.bayData);
    if (!rows.length) return;

    const tcgs = rows.map((row) => getRowTcg(p, blp, row));

    LASHING_BRIDGE_SIDES.forEach((side) => {
      const bridge = info[side];
      if (!bridge) return;

      const rowsGeometry: ILashingBridgeRowGeometry[] = [];

      resolveLashingBridgeRows(bridge, rows).forEach((resolved, idx) => {
        const heightAboveBottom = lashingBridgeHeightInMm(resolved, tierHeightInMm);
        const tcg = tcgs[idx];
        if (heightAboveBottom <= 0 || tcg === undefined) return;

        const stackBottomBase = getRowStackBottomBase(p, blp, resolved.row);
        if (stackBottomBase === undefined) return;

        const top = stackBottomBase + heightAboveBottom;

        rowsGeometry.push({
          row: resolved.row,
          tcg,
          halfWidth: getHalfWidth(tcgs, idx),
          stackBottomBase,
          heightAboveBottom,
          top,
          ...getLashingBridgeFrameLevels(stackBottomBase, top, tierHeightInMm, railHeightTiers),
        });
      });

      if (!rowsGeometry.length) return;

      const lcgIsDefault = bridge.lcg === undefined;
      const lcg = lcgIsDefault
        ? getDefaultLashingBridgeLcg(p, blp.isoBay, side, {
            thicknessInMm: thickness,
            tierHeightInMm,
            structureHeightInMm: Math.max(
              ...rowsGeometry.map((r) => r.structureTop - r.stackBottomBase),
            ),
          })
        : bridge.lcg;
      if (lcg === undefined || !Number.isFinite(lcg)) return;

      const lcgCenter = lcg;

      result.push({
        isoBay: blp.isoBay,
        side,
        lcg,
        lcgIsDefault,
        lcgCenter,
        thickness,
        topWalkwayDepth,
        tierHeight: tierHeightInMm,
        rows: rowsGeometry,
        minBottomBase: Math.min(...rowsGeometry.map((r) => r.stackBottomBase)),
        maxTop: Math.max(...rowsGeometry.map((r) => r.top)),
        maxStructureTop: Math.max(...rowsGeometry.map((r) => r.structureTop)),
      });
    });
  });

  return result;
}

/**
 * LCG of a lashing bridge whose OVD doesn't define one, from its slot ({@link getLashingBridgeSlots}):
 * - in the middle of the gap with the neighbour block of bays, when there is no vessel part in
 *   between, so it stands between both stacks;
 * - otherwise (the ends of the ship, a vessel part like a deckhouse follows, or the blocks touch)
 *   it stands off the end of the block by half the typical gap between blocks, where it would be
 *   if another block followed. Without such gaps, by half its thickness plus a clearance.
 *
 * A bridge stored on a position that isn't a slot (old data) uses the neighbour bay: centered when
 * only one bridge fits in between, standing off the end of the bay otherwise.
 *
 * The ends come from the 20' and 40' slots: longer containers overhang the bay ends, but they
 * are usually stowed on top, above the bridges. When a bay stows them lower than the top of the
 * bridge structure (handrail included), the ends of those containers are used instead
 * ({@link getLowLongContainerEnds}), so the bridge clears them.
 */
export function getDefaultLashingBridgeLcg(
  p: Pick<
    BayLcgVcgTcgAndPairings,
    | "lcgsBy20Bay"
    | "bayLevelPositionsDict"
    | "vesselPartsData"
    | "sizeSummary"
    | "averageContainerHeightInMm"
  >,
  isoBay: IBayPattern,
  side: TLashingBridgeSide,
  {
    thicknessInMm = LASHING_BRIDGE_THICKNESS_IN_MM,
    tierHeightInMm = p.averageContainerHeightInMm,
    structureHeightInMm,
  }: {
    thicknessInMm?: number;
    /** Defaults to the average container height of the calculations (8'6") */
    tierHeightInMm?: number;
    /**
     * Height of the bridge structure (handrail included) over the stack bottom, in mm.
     * Defaults to the one of the bridge stored in the bay ({@link getLashingBridgeStructureHeight})
     */
    structureHeightInMm?: number;
  } = {},
): number | undefined {
  const height =
    structureHeightInMm ?? getLashingBridgeStructureHeight(p, isoBay, side, tierHeightInMm);
  /** `end` of `bays` on `endSide`, pushed out by the longer containers stowed below the bridge top */
  const clearEnd = (end: number, bays: IBayPattern[], endSide: TLashingBridgeSide) => {
    const long = height > 0 ? getLowLongContainerEnds(p, bays, height, tierHeightInMm) : undefined;
    if (!long) return end;
    return endSide === "fore" ? Math.max(end, long.fore) : Math.min(end, long.aft);
  };
  const otherSide: TLashingBridgeSide = side === "fore" ? "aft" : "fore";

  const slots = getLashingBridgeSlots(p);
  const sign = side === "fore" ? 1 : -1;
  const typicalGap = getTypicalLashingBridgeGap(slots);
  const standOff =
    typicalGap !== undefined
      ? typicalGap * 0.5
      : thicknessInMm * 0.5 + LASHING_BRIDGE_END_CLEARANCE_IN_MM;

  const slot = slots.find((s) => s.isoBay === isoBay && s.side === side);
  if (slot) {
    if (slot.end === undefined) return undefined;
    const end = clearEnd(slot.end, splitBlock(slot.block), side);
    const centered =
      !slot.partBetween &&
      slot.gap !== undefined &&
      slot.gap > 0 &&
      slot.neighbourEnd !== undefined &&
      slot.neighbourBlock !== undefined;
    if (!centered) return end + sign * standOff;

    const neighbourEnd = clearEnd(
      slot.neighbourEnd as number,
      splitBlock(slot.neighbourBlock as string),
      otherSide,
    );
    return (end + neighbourEnd) * 0.5;
  }

  // Not an offered position: use the neighbour bay
  const ends = getBayEnds(p, isoBay);
  if (!ends) return undefined;
  const end = clearEnd(side === "fore" ? ends.fore : ends.aft, [isoBay], side);

  const nextBay = pad3(Number(isoBay) - sign * 2) as IBayPattern;
  const next = Number(nextBay) >= 1 ? getBayEnds(p, nextBay) : undefined;
  const nextEnd = next
    ? clearEnd(side === "fore" ? next.aft : next.fore, [nextBay], otherSide)
    : undefined;
  const gap = nextEnd !== undefined ? sign * (nextEnd - end) : undefined;

  return gap !== undefined && gap > 0 && gap < LASHING_BRIDGE_MIN_DOUBLE_GAP_IN_MM
    ? (end + (nextEnd as number)) * 0.5
    : end + sign * standOff;
}

/**
 * Height of the structure (handrail included) of the bridge stored on a bay and side, over the
 * stack bottom, in mm: the highest of its rows. 0 when there is no such bridge.
 */
export function getLashingBridgeStructureHeight(
  p: Pick<BayLcgVcgTcgAndPairings, "bayLevelPositionsDict" | "sizeSummary">,
  isoBay: IBayPattern,
  side: TLashingBridgeSide,
  tierHeightInMm: number,
  railHeightTiers = LASHING_BRIDGE_RAIL_HEIGHT_TIERS,
): number {
  const blp = p.bayLevelPositionsDict[`${isoBay}-${BayLevelEnum.ABOVE}`];
  const bridge = blp?.lashingBridges?.[side];
  if (!bridge) return 0;

  const heights = resolveLashingBridgeRows(bridge, getBayLevelRows(p.sizeSummary, blp.bayData))
    .map((r) => lashingBridgeHeightInMm(r, tierHeightInMm))
    .filter((h) => h > 0);

  return heights.length ? Math.max(...heights) + tierHeightInMm * railHeightTiers : 0;
}

/** Bays of a block label (`"005-007"` or `"009"`) */
function splitBlock(block: string): IBayPattern[] {
  return block.split("-") as IBayPattern[];
}

/**
 * Frame of a lashing bridge row, as drawn by the side view and the 3D view:
 * - the top walkway is at the bridge top (`tiers`/`height`);
 * - the handrail rises `railHeightTiers` over it: that is the top of the structure
 *   (a 3-tier bridge tops out at 3.5 tiers);
 * - there is a walkway at every tier level below the top one, skipping one closer than a quarter tier.
 */
export function getLashingBridgeFrameLevels(
  bottom: number,
  top: number,
  tierHeight: number,
  railHeightTiers: number,
): { structureTop: number; walkways: number[] } {
  const walkways: number[] = [];
  if (tierHeight > 0)
    for (let y = bottom + tierHeight; y < top - tierHeight * 0.25; y += tierHeight)
      walkways.push(y);
  walkways.push(top);

  return { structureTop: top + tierHeight * railHeightTiers, walkways };
}

function getRowTcg(
  p: BayLcgVcgTcgAndPairings,
  blp: BaySizesAndCgs,
  row: IRowPattern,
): number | undefined {
  const tcg =
    blp.perRowAverageBayTcg.get(row) ??
    blp.tcgByRow.get(row) ??
    p.getMissingTcg(row, blp.isoBay, BayLevelEnum.ABOVE);

  return tcg !== undefined && Number.isFinite(tcg) ? tcg : undefined;
}

function getRowStackBottomBase(
  p: BayLcgVcgTcgAndPairings,
  blp: BaySizesAndCgs,
  row: IRowPattern,
): number | undefined {
  const rowBottom = blp.rowBottoms.get(row);
  const minTier = Number.isFinite(blp.minTier) ? blp.minTier : p.sizeSummary.minAboveTier;

  const candidates = [
    rowBottom?.bottomBase,
    rowBottom?.bottomTier !== undefined ? p.getMissingBottomBase(rowBottom.bottomTier) : undefined,
    minTier !== undefined ? p.getMissingBottomBase(pad2(minTier)) : undefined,
    p.aboveBottomBases[blp.isoBay],
  ];

  return candidates.find((v): v is number => v !== undefined && Number.isFinite(v));
}

/** Half the distance to the nearest neighbour row, so the bridge is continuous across the rows */
function getHalfWidth(tcgs: Array<number | undefined>, idx: number): number {
  const tcg = tcgs[idx];
  if (tcg === undefined) return CONTAINER_WIDTH_IN_MMM * 0.5;

  const distances = [tcgs[idx - 1], tcgs[idx + 1]]
    .filter((t): t is number => t !== undefined)
    .map((t) => Math.abs(t - tcg))
    .filter((d) => d > 0);

  return distances.length ? Math.min(...distances) * 0.5 : CONTAINER_WIDTH_IN_MMM * 0.5;
}
